/**
 * Tableau de bord des statistiques FTPE pour Admin — UI ultra tech
 * Affiche : KPI animés, funnel de conversion, scheduler, évolution mensuelle, territoires
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Users, CheckCircle, XCircle, Clock, RefreshCw, Bell, Calendar, TrendingUp, MapPin, AlertTriangle, Mail, Loader2, DollarSign, Shield, Activity, Zap, Target } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return 'Jamais';
  try {
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
};

const formatMonth = (monthStr) => {
  try {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleString('fr-FR', { month: 'short', year: '2-digit' });
  } catch { return monthStr; }
};

/** Mini-sparkline SVG (lit la dernière colonne `byMonth` si dispo) */
function Sparkline({ data, color = '#10b981' }) {
  if (!data || data.length < 2) return null;
  const values = data.map((d) => d.count || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 80, h = 24;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="opacity-90">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points={`0,${h} ${points} ${w},${h}`} fill={color} opacity="0.15" />
    </svg>
  );
}

export default function FTPEStatsDashboard() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggeringCheck, setTriggeringCheck] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ftpe-adhesion/admin/statistics`, { headers: getAuthHeader() });
      if (response.data.success) {
        setStats(response.data.stats);
        setScheduler(response.data.scheduler);
      }
    } catch {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleTriggerCheck = async () => {
    try {
      setTriggeringCheck(true);
      const response = await axios.post(`${API}/ftpe-adhesion/admin/trigger-expiration-check`, {}, { headers: getAuthHeader() });
      if (response.data.success) {
        toast.success(`Vérification effectuée — ${response.data.emails_sent} rappel(s) envoyé(s)`);
        fetchStats();
      } else {
        toast.error(response.data.message || 'Erreur lors de la vérification');
      }
    } catch {
      toast.error('Erreur lors du déclenchement de la vérification');
    } finally {
      setTriggeringCheck(false);
    }
  };

  const conversion = useMemo(() => {
    const total = stats?.total || 0;
    const active = stats?.active || 0;
    const pending = stats?.pending || 0;
    const expired = stats?.expired || 0;
    return {
      tauxActivation: total > 0 ? Math.round((active / total) * 100) : 0,
      tauxAttente: total > 0 ? Math.round((pending / total) * 100) : 0,
      tauxExpiration: total > 0 ? Math.round((expired / total) * 100) : 0,
    };
  }, [stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
        <span className="ml-3 text-base font-medium text-slate-700">Chargement du tableau de bord FTPE…</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header gradient ultra tech avec metrics */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white p-6 shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 50%), radial-gradient(circle at 20% 80%, #60a5fa 0%, transparent 50%)' }} />
        <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500 rounded-full blur-3xl opacity-20 -translate-y-32 translate-x-32" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg flex-shrink-0">
              <Shield className="h-7 w-7 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-2xl font-bold leading-tight">Tableau de bord FTPE</h2>
                <Badge className="bg-emerald-500 text-white border-0 gap-1 px-2 animate-pulse font-semibold">
                  <span className="w-1.5 h-1.5 bg-white rounded-full" /> LIVE
                </Badge>
              </div>
              <p className="text-sm text-white/70">Statistiques temps réel des adhésions FTPE Outremer</p>
            </div>
          </div>
          <Button onClick={fetchStats} variant="secondary" size="sm" className="bg-white/20 hover:bg-white/30 backdrop-blur-md text-white border-0 gap-2">
            <RefreshCw className="h-4 w-4" /> Actualiser
          </Button>
        </div>

        {/* Mini metrics integrés au header */}
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          {[
            { label: 'Total', val: stats?.total || 0, color: 'from-blue-400 to-cyan-300', icon: Users },
            { label: 'Actives', val: stats?.active || 0, color: 'from-emerald-400 to-green-300', icon: CheckCircle },
            { label: 'En attente', val: stats?.pending || 0, color: 'from-amber-400 to-yellow-300', icon: Clock },
            { label: 'Expirées', val: stats?.expired || 0, color: 'from-rose-400 to-pink-300', icon: XCircle },
          ].map(({ label, val, color, icon: Icon }) => (
            <div key={label} className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-3 py-2.5 hover:bg-white/15 transition-all">
              <div className="flex items-center gap-2 mb-1">
                <Icon className="h-3.5 w-3.5 text-white/70" />
                <span className="text-[10px] uppercase tracking-wider font-bold text-white/70">{label}</span>
              </div>
              <p className={`text-3xl font-bold bg-gradient-to-r ${color} bg-clip-text text-transparent leading-none`}>{val}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Funnel de conversion + Revenus */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Funnel ultra tech */}
        <Card className="lg:col-span-2 border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b py-3">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Target className="h-4 w-4 text-white" />
              </div>
              Funnel de conversion FTPE
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            {[
              { label: 'Total adhésions créées', val: stats?.total || 0, pct: 100, color: 'from-blue-500 to-cyan-500', icon: Users },
              { label: 'En attente de paiement', val: stats?.pending || 0, pct: conversion.tauxAttente, color: 'from-amber-500 to-orange-500', icon: Clock },
              { label: 'Adhésions actives', val: stats?.active || 0, pct: conversion.tauxActivation, color: 'from-emerald-500 to-green-500', icon: CheckCircle },
              { label: 'Expirées (à renouveler)', val: stats?.expired || 0, pct: conversion.tauxExpiration, color: 'from-rose-500 to-pink-500', icon: XCircle },
            ].map(({ label, val, pct, color, icon: Icon }) => (
              <div key={label} className="space-y-1.5" data-testid={`funnel-${label.replace(/\s/g, '-')}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 font-medium text-slate-700">
                    <Icon className="h-3.5 w-3.5 text-slate-500" />
                    {label}
                  </span>
                  <span className="font-bold text-slate-900">
                    {val}
                    <span className="text-xs text-slate-500 font-mono ml-2">({pct}%)</span>
                  </span>
                </div>
                <div className="relative h-3 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 bg-gradient-to-r ${color} transition-all duration-1000 rounded-full shadow-sm`}
                    style={{ width: `${pct}%` }}
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.4)_50%,transparent_100%)] bg-[length:200%_100%] animate-pulse opacity-50" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Revenus + sparkline */}
        <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-50 via-emerald-50/30 to-white border-l-4 border-l-emerald-500 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold flex items-center justify-between">
              <span className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-white" />
                </div>
                Revenu FTPE
              </span>
              <Sparkline data={stats?.byMonth} color="#10b981" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent leading-none">
              {(stats?.revenueTotal || 0).toLocaleString('fr-FR')}
              <span className="text-2xl ml-1">€</span>
            </p>
            <p className="text-xs text-emerald-600 font-semibold mt-2 flex items-center gap-1">
              <Activity className="h-3 w-3" />
              {stats?.active || 0} adhésions actives
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2 pt-3 border-t border-emerald-100">
              <div>
                <p className="text-[10px] text-emerald-600 uppercase tracking-wider font-bold">J-7 expirations</p>
                <p className="text-xl font-bold text-emerald-700">{stats?.expiringIn7Days || 0}</p>
              </div>
              <div>
                <p className="text-[10px] text-emerald-600 uppercase tracking-wider font-bold">J-30 expirations</p>
                <p className="text-xl font-bold text-emerald-700">{stats?.expiringIn30Days || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scheduler & Évolution mensuelle */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Scheduler */}
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b py-3">
            <CardTitle className="text-sm font-bold flex items-center justify-between">
              <span className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Bell className="h-4 w-4 text-white" />
                </div>
                Scheduler de rappels
              </span>
              <Badge className={scheduler?.enabled ? 'bg-emerald-500 gap-1' : 'bg-slate-400 gap-1'}>
                <span className={`w-1.5 h-1.5 ${scheduler?.enabled ? 'bg-white animate-pulse' : 'bg-white/70'} rounded-full`} />
                {scheduler?.enabled ? 'Actif' : 'Inactif'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {/* Timeline activity */}
            <div className="space-y-2.5 pb-3 border-b">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Rappels envoyés</p>
                  <p className="text-2xl font-bold text-slate-900 leading-none mt-0.5">{stats?.remindersSent || 0}</p>
                </div>
                <Badge variant="outline" className="text-[10px] font-mono border-purple-200 text-purple-600 bg-purple-50 font-semibold">
                  J-30, J-15, J-7, J-1
                </Badge>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-4 w-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Programmation</p>
                  <p className="text-sm font-mono font-semibold text-slate-700 truncate">{scheduler?.schedule || 'Non configuré'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-4 w-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Dernière exécution</p>
                  <p className="text-sm font-medium text-slate-700">{formatDate(scheduler?.last_run)}</p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleTriggerCheck}
              disabled={triggeringCheck}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md gap-2"
            >
              {triggeringCheck ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Vérification en cours…</>
              ) : (
                <><Zap className="h-4 w-4" /> Déclencher vérification maintenant</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Évolution mensuelle — bar chart vertical */}
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b py-3">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              Évolution mensuelle
              <Badge variant="outline" className="ml-auto text-[10px] font-mono">6 derniers mois</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {stats?.byMonth && stats.byMonth.length > 0 ? (
              <div className="flex items-end justify-between gap-2 h-44 px-1">
                {stats.byMonth.map((item, idx) => {
                  const max = Math.max(...stats.byMonth.map((m) => m.count), 1);
                  const heightPct = (item.count / max) * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full flex-1 flex items-end">
                        <div
                          className="w-full bg-gradient-to-t from-indigo-600 via-purple-500 to-pink-400 rounded-t-md hover:opacity-80 transition-opacity relative group cursor-pointer"
                          style={{ height: `${heightPct || 4}%` }}
                          title={`${item.count} adhésions`}
                        >
                          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap">
                            {item.count}
                          </span>
                        </div>
                      </div>
                      <span className="text-[10px] font-medium text-slate-500 truncate">{formatMonth(item.month)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400 text-sm">Aucune donnée disponible</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Répartition par territoire */}
      <Card className="border-0 shadow-md overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b py-3">
          <CardTitle className="text-sm font-bold flex items-center justify-between">
            <span className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <MapPin className="h-4 w-4 text-white" />
              </div>
              Répartition par territoire
            </span>
            <Badge variant="outline" className="text-[10px] font-mono border-teal-200 text-teal-700 bg-white text-slate-800 font-semibold">
              {stats?.byTerritory ? Object.keys(stats.byTerritory).length : 0} territoires
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {stats?.byTerritory && Object.entries(stats.byTerritory).length > 0 ? (
            (() => {
              const sorted = Object.entries(stats.byTerritory).sort((a, b) => b[1] - a[1]);
              const total = sorted.reduce((s, [, v]) => s + v, 0);
              return (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {sorted.map(([territoire, count], idx) => {
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    const isTop = idx === 0;
                    return (
                      <div
                        key={territoire}
                        className={`relative p-3 rounded-xl border transition-all hover:shadow-md ${isTop ? 'bg-gradient-to-br from-teal-500 to-cyan-600 text-white border-teal-600' : 'bg-white border-teal-100 hover:border-teal-300'}`}
                      >
                        {isTop && (
                          <div className="absolute -top-1.5 -right-1.5 bg-amber-400 text-amber-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                            #1
                          </div>
                        )}
                        <div className="flex items-baseline justify-between">
                          <p className={`text-2xl font-bold leading-none ${isTop ? 'text-white' : 'text-teal-700'}`}>{count}</p>
                          <span className={`text-[10px] font-mono font-bold ${isTop ? 'text-white/80' : 'text-teal-500'}`}>{pct}%</span>
                        </div>
                        <p className={`text-[11px] font-semibold mt-1.5 truncate ${isTop ? 'text-white/90' : 'text-slate-700'}`}>{territoire}</p>
                        <div className={`mt-2 h-1 rounded-full overflow-hidden ${isTop ? 'bg-white/30' : 'bg-teal-100'}`}>
                          <div
                            className={`h-full rounded-full transition-all duration-1000 ${isTop ? 'bg-white' : 'bg-gradient-to-r from-teal-400 to-cyan-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            <div className="text-center py-12 text-slate-400 text-sm">Aucune donnée disponible</div>
          )}
        </CardContent>
      </Card>

      {/* Alertes — seulement si pertinentes */}
      {(stats?.expiringIn7Days > 0 || stats?.expiringIn30Days > 0) && (
        <Card className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-md">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-amber-900 text-sm">Expirations à venir</p>
                <p className="text-xs text-amber-700">
                  <strong>{stats.expiringIn7Days || 0}</strong> adhésion(s) expirent dans 7 jours · <strong>{stats.expiringIn30Days || 0}</strong> dans 30 jours
                </p>
              </div>
              <Button
                size="sm"
                onClick={handleTriggerCheck}
                disabled={triggeringCheck}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-sm gap-2"
              >
                <Bell className="h-4 w-4" /> Envoyer les rappels
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
